import { EventType } from "@/analytics/analytics-events";
import { GotoAnnotationProperties } from "@/analytics/annotation-events-properties";
import { useAnnotationAssignees } from "@/hooks/use-annotation-assignees";
import { useGoToAnnotation } from "@/hooks/use-go-to-annotation";
import { selectBackgroundTaskForIElement } from "@/store/background-tasks/background-tasks-selector";
import { useAppSelector } from "@/store/store-hooks";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert, GUID } from "@faro-lotv/foundation";
import {
  AnnotationStatus,
  isIElementAttachment,
  isIElementExternalMarkup,
  isIElementMarkup,
  isIElementMarkupDueTime,
  isIElementMarkupState,
} from "@faro-lotv/ielement-types";
import {
  selectChildDepthFirst,
  selectChildrenDepthFirst,
  selectIElement,
} from "@faro-lotv/project-source";
import { GetTopicResponse, isAnnotationStatus } from "@faro-lotv/service-wires";
import { SxProps } from "@mui/system";
import { isEqual } from "es-toolkit";
import { useCallback, useMemo, type JSX } from "react";
import { Tag } from "../annotations/annotation-viewer/annotation-viewer-types";
import { getGenericUserInfo } from "../annotations/external-annotation-utils";
import { isTagReadable } from "../tags/tag-accessibility";
import { ResponsiveCard } from "./annotation-list-card/responsive-card";
import { ResponsiveCardVariant } from "./annotation-list-card/responsive-card-variant";
import { AiMarkupData } from "./annotation-panel";

export type AnnotationListCardProps = {
  /** GUID of the annotation to render the card for */
  markupId: GUID;

  /** The fetched data if the markup is external */
  externalMarkupData?: GetTopicResponse;

  /** The AI data for the markup */
  aiMarkupData?: AiMarkupData;

  /** The card variant to use for the current list width */
  variant: ResponsiveCardVariant;

  /** The extra styling to apply to the card container */
  sx?: SxProps;
};

/** @returns a single responsive card to interact with an annotation in the list */
export function AnnotationListCard({
  markupId,
  externalMarkupData,
  aiMarkupData,
  variant,
  sx,
}: AnnotationListCardProps): JSX.Element {
  const element = useAppSelector(selectIElement(markupId));
  assert(!!element, "Markup does not exist");

  const externalMarkup = useAppSelector(
    selectChildDepthFirst(element, isIElementExternalMarkup),
  );
  const markup = useAppSelector(
    selectChildDepthFirst(element, isIElementMarkup),
  );
  assert(
    (externalMarkup && externalMarkupData) ?? markup,
    "Input element is not valid markup",
  );

  const finalMarkup = externalMarkup ?? markup;
  assert(finalMarkup, "Invalid markup data");

  const attachments = useAppSelector(
    selectChildrenDepthFirst(markup, isIElementAttachment),
    isEqual,
  );

  const tags: Tag[] | undefined = useMemo(
    () =>
      markup?.labels?.filter(isTagReadable).map((label) => ({
        id: label.id,
        label: label.name,
        labelType: label.labelType,
      })),
    [markup?.labels],
  );

  const stateElement = useAppSelector(
    selectChildDepthFirst(markup, isIElementMarkupState),
  );
  const state = isAnnotationStatus(stateElement?.value)
    ? stateElement.value
    : AnnotationStatus.Unclassified;

  const mainAssignee = useAnnotationAssignees(markup).at(0);

  const goToAnnotation = useGoToAnnotation();
  const goTo = useCallback(() => {
    Analytics.track<GotoAnnotationProperties>(EventType.gotoAnnotation, {
      via: "annotation list",
    });

    goToAnnotation(finalMarkup);
  }, [finalMarkup, goToAnnotation]);

  const dueDate = useAppSelector(
    selectChildDepthFirst(markup, isIElementMarkupDueTime),
  );

  const finalDueDate = externalMarkupData
    ? externalMarkupData.due_date
    : dueDate?.value;

  const backgroundTask = useAppSelector(
    selectBackgroundTaskForIElement(markupId),
  );

  return (
    <ResponsiveCard
      attachments={externalMarkup ? [] : attachments}
      name={externalMarkupData ? externalMarkupData.title : markup?.name}
      tags={tags}
      state={state}
      mainAssignee={
        externalMarkupData
          ? getGenericUserInfo(externalMarkupData.assigned_to)
          : mainAssignee
      }
      dueDate={finalDueDate ?? undefined}
      markup={finalMarkup}
      aiMarkupData={aiMarkupData}
      variant={variant}
      onGoTo={goTo}
      sx={sx}
      isLoading={!!backgroundTask}
    />
  );
}
